/**
 * TaskListView -- Interactive task list with create, status change, and delete.
 * Uses shared TaskDetailRenderer for expand sections (eliminates duplication).
 *
 * @module presentation/views/TaskListView
 */

import { TaskApiClient } from '../../infrastructure/api/TaskApiClient.js';
import { camposPuertaHtml, engancharCamposPuerta, pedirPuertaEnDialogo, claveSugerida } from '../shared/camposPuerta.js';
import { motivoPuerta } from '../../application/tareas/puertaTarea.js';
import { estaBloqueada, bloqueadaPor, etiquetaBloqueo, motivoTarea } from '../../application/tareas/bloqueoTarea.js';
import { ProjectApiClient } from '../../infrastructure/api/ProjectApiClient.js';
import { RewardService } from '../../application/RewardService.js';
import { avisarCierre } from '../shared/avisoDeCierre.js';
import { avisar, confirmar } from '../shared/nxDialogo.js';
import { renderTaskDetailHTML, bindTaskDetailEvents, normalizeTask, renderTagBadges } from '../shared/TaskDetailRenderer.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

const STATUS_LABELS = {
  pending: 'pendiente',
  in_progress: 'en progreso',
  completed: 'completada',
  cancelled: 'cancelada'
};

const STATUS_COLORS = {
  pending: '#d29922',
  in_progress: '#58a6ff',
  completed: '#238636',
  cancelled: '#6e7681'
};

const STATUSES = ['pending', 'in_progress', 'completed', 'cancelled'];

let currentPage = 1;
let currentFilter = 'in_progress';
let currentSort = 'createdAt';
let currentSortDir = 'desc';
let currentTypeFilter = 'all'; // 'all' | 'templates' | 'regular'
let currentTagFilters = [];
let cachedProjects = [];

// Cache de tareas expandidas: evita llamadas extra al API en operaciones de steps
const taskDetailCache = new Map();

function badge(status) {
  const color = STATUS_COLORS[status] || '#6e7681';
  const label = STATUS_LABELS[status] || status;
  return `<span class="status-badge" style="background:${color}22;color:${color}">${label}</span>`;
}

function statusOptions(current) {
  return STATUSES
    .filter(s => s !== current)
    .map(s => `<option value="${s}">${STATUS_LABELS[s]}</option>`)
    .join('');
}

function taskRow(t, templateStats) {
  const tmpl = (t.tags || []).includes('template');
  const ver = tmpl && t.metadata?.version ? `<span class="version-badge">v${t.metadata.version}</span>` : '';
  const stats = tmpl && templateStats?.get(t.id);
  const effectHtml = stats ? `<span class="template-effectiveness">${stats.used} uso${stats.used !== 1 ? 's' : ''} | ${stats.completed} completada${stats.completed !== 1 ? 's' : ''}</span>` : '';
  const useBtn = tmpl ? `<button class="btn-use-template" data-use-template="${t.id}" title="Crear tarea desde plantilla">\u25B6 Usar</button>` : '';
  const descPreview = t.description
    ? (t.description.length > 120 ? t.description.slice(0, 120) + '\u2026' : t.description).replace(/</g, '&lt;')
    : '';
  return `
    <div class="artifact-item task-row${tmpl ? ' task-row-template' : ''}" data-id="${t.id}" style="display:flex;flex-direction:column;align-items:stretch">
      <div style="display:flex;align-items:center;width:100%;gap:8px;cursor:pointer" data-toggle-task="${t.id}">
        <span style="font-size:0.875rem;color:#6e7681;flex-shrink:0;transition:transform 0.15s" data-expand-icon="${t.id}">\u25B8</span>
        <div class="task-info" style="flex:1;min-width:0;overflow:hidden">
          <div class="artifact-item-title" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">
            ${t.title || '(sin titulo)'}
            ${renderTagBadges(t.tags)}
            ${ver}
          </div>
          ${descPreview ? `<div class="nx-task-desc-preview" style="font-size:0.875rem;color:#8b949e;margin-top:2px;line-height:1.3;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${descPreview}</div>` : ''}
          <div class="artifact-item-meta">${t.project || 'General'} &middot; ${formatearFecha(t.createdAt, {}, { locale: 'es' })}${t.dueDate ? ` &middot; <span class="due-date-tag" title="Vencimiento">${formatearFecha(t.dueDate, {}, { locale: 'es' })}</span>` : ''} ${effectHtml}</div>
        </div>
        <div class="task-actions" style="flex-shrink:0;display:flex;align-items:center;gap:4px">
          ${useBtn}
          ${tmpl
            ? `<span class="status-badge" style="background:#d2992222;color:#d29922;font-size:0.875rem">plantilla</span>`
            : `${estaBloqueada(t) ? `<span class="status-badge" data-bloqueada style="background:#f8514922;color:#f85149" title="Espera a ${bloqueadaPor(t).join(', ')}: se libera sola cuando se cierren o salga su VIVO">&#128274; ${etiquetaBloqueo(t)}</span>` : badge(t.status)}
              <select class="status-select" data-id="${t.id}" title="Cambiar estado">
                <option value="" disabled selected>Cambiar</option>
                ${statusOptions(t.status)}
              </select>`}
          <button class="btn-icon btn-delete" data-id="${t.id}" title="Eliminar">&times;</button>
        </div>
      </div>
      <div id="nx-task-detail-${t.id}" hidden style="padding:10px 0 4px;border-top:1px solid #21262d;margin-top:8px;width:100%">
        <p class="empty-state" style="font-size:0.875rem">Cargando detalle...</p>
      </div>
    </div>`;
}

export async function renderTaskList(container) {
  currentFilter = 'in_progress';
  currentTypeFilter = 'all';
  currentTagFilters = [];
  taskDetailCache.clear();

  const projRes = await ProjectApiClient.list(1, 50);
  cachedProjects = projRes.data?.data?.items || projRes.data?.items || [];

  container.innerHTML = `
    <section class="artifact-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>Tareas</h1>
        </div>
        <button class="btn-primary btn-sm" id="btn-new-task">+ Nueva tarea</button>
      </div>

      <div class="task-filter-bar" id="nx-task-filters">
        <button class="btn-filter" data-filter="all">Todas</button>
        <button class="btn-filter" data-filter="pending">Pendientes</button>
        <button class="btn-filter active" data-filter="in_progress">En progreso</button>
        <button class="btn-filter" data-filter="completed">Completadas</button>
        <button class="btn-filter" data-filter="cancelled">Canceladas</button>
      </div>

      <div class="task-sort-bar" id="nx-task-sort">
        <label class="sort-label">Ordenar:</label>
        <select id="nx-sort-field" class="form-input form-input-sm">
          <option value="createdAt" selected>Fecha creaci\u00f3n</option>
          <option value="dueDate">Fecha vencimiento</option>
          <option value="priority">Prioridad</option>
        </select>
        <button id="nx-sort-dir" class="btn-ghost btn-sm sort-dir-btn" title="Cambiar direcci\u00f3n">\u2193 Recientes</button>
      </div>

      <div class="task-tag-filter-bar" id="nx-tag-filters">
        <span class="filter-label">Tipo:</span>
        <button class="btn-tag-filter active" data-type="all">Todas</button>
        <button class="btn-tag-filter" data-type="templates">Plantillas</button>
        <button class="btn-tag-filter" data-type="regular">Regulares</button>
        <span class="filter-label" style="margin-left:8px">Tags:</span>
        <span id="nx-tag-chips"></span>
      </div>

      <div id="nx-task-form-area" hidden>
        <form id="nx-task-form" class="inline-form" style="flex-wrap:wrap">
          <input type="text" id="nx-task-title" placeholder="Titulo de la tarea" required class="form-input" style="flex:1;min-width:200px">
          <select id="nx-task-project" class="form-input form-input-sm" required>
            <option value="" disabled selected>Proyecto *</option>
            ${cachedProjects.map(p =>
              `<option value="${p.projectKey}">${p.projectKey} (${p.displayName || ''})</option>`
            ).join('')}
          </select>
          <textarea id="nx-task-desc" placeholder="Descripcion (opcional)" class="form-input" rows="2" style="flex-basis:100%;resize:vertical;margin-top:6px"></textarea>
          <div style="display:flex;gap:6px;align-items:center;flex-basis:100%;margin-top:6px">
            <input type="text" id="nx-task-tags" placeholder="Tags: migration, api..." class="form-input" style="flex:1;font-size:0.875rem">
            <label style="font-size:0.875rem;color:#6e7681;display:flex;align-items:center;gap:4px;cursor:pointer;white-space:nowrap">
              <input type="checkbox" id="nx-task-is-template" style="accent-color:#d29922">
              Plantilla
            </label>
            <input type="text" id="nx-task-version" placeholder="v1.0" class="form-input" style="width:60px;font-size:0.875rem;display:none">
          </div>
          ${camposPuertaHtml('nx-task-puerta')}
          <button type="submit" class="btn-primary btn-sm">Crear</button>
          <button type="button" class="btn-ghost btn-sm" id="btn-cancel-task">Cancelar</button>
        </form>
        <p id="nx-task-form-error" class="auth-error" hidden></p>
      </div>

      <div class="artifact-list" id="nx-task-list">
        <p class="empty-state">Cargando tareas...</p>
      </div>
      <div id="nx-task-pager" class="pager"></div>
    </section>`;

  setupFilterHandlers();
  setupSortHandlers();
  setupTagFilterHandlers();
  setupFormHandlers();
  setupListHandlers();
  await loadPage(currentPage);
}

function setupFilterHandlers() {
  const $bar = document.getElementById('nx-task-filters');
  $bar.addEventListener('click', (e) => {
    const btn = e.target.closest('.btn-filter');
    if (!btn) return;
    $bar.querySelectorAll('.btn-filter').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    currentFilter = btn.dataset.filter;
    currentPage = 1;
    loadPage(1);
  });
}

const SORT_DIR_LABELS = {
  createdAt: { desc: '\u2193 Recientes', asc: '\u2191 Antiguas' },
  dueDate:   { desc: '\u2193 Lejanas',   asc: '\u2191 Pr\u00f3ximas' },
  priority:  { desc: '\u2193 Mayor',     asc: '\u2191 Menor' }
};

const PRIORITY_ORDER = { urgent: 4, high: 3, medium: 2, low: 1 };

function setupSortHandlers() {
  const $field = document.getElementById('nx-sort-field');
  const $dir = document.getElementById('nx-sort-dir');

  $field.addEventListener('change', () => {
    currentSort = $field.value;
    currentSortDir = currentSort === 'dueDate' ? 'asc' : 'desc';
    $dir.textContent = SORT_DIR_LABELS[currentSort]?.[currentSortDir] || '\u2193';
    currentPage = 1;
    loadPage(1);
  });

  $dir.addEventListener('click', () => {
    currentSortDir = currentSortDir === 'desc' ? 'asc' : 'desc';
    $dir.textContent = SORT_DIR_LABELS[currentSort]?.[currentSortDir] || '\u2193';
    currentPage = 1;
    loadPage(1);
  });
}

function setupTagFilterHandlers() {
  const $bar = document.getElementById('nx-tag-filters');
  $bar.addEventListener('click', (e) => {
    // Type filter buttons
    const typeBtn = e.target.closest('[data-type]');
    if (typeBtn) {
      $bar.querySelectorAll('[data-type]').forEach(b => b.classList.remove('active'));
      typeBtn.classList.add('active');
      currentTypeFilter = typeBtn.dataset.type;
      currentPage = 1;
      loadPage(1);
      return;
    }
    // Tag chip toggle
    const tagChip = e.target.closest('[data-tag-chip]');
    if (tagChip) {
      const tag = tagChip.dataset.tagChip;
      tagChip.classList.toggle('active');
      if (tagChip.classList.contains('active')) {
        if (!currentTagFilters.includes(tag)) currentTagFilters.push(tag);
      } else {
        currentTagFilters = currentTagFilters.filter(t => t !== tag);
      }
      currentPage = 1;
      loadPage(1);
    }
  });
}

function setupFormHandlers() {
  const $btn = document.getElementById('btn-new-task');
  const $area = document.getElementById('nx-task-form-area');
  const $form = document.getElementById('nx-task-form');
  const $cancel = document.getElementById('btn-cancel-task');
  const $error = document.getElementById('nx-task-form-error');

  $btn.onclick = () => {
    $area.hidden = !$area.hidden;
    if (!$area.hidden) document.getElementById('nx-task-title').focus();
  };

  $cancel.onclick = () => { $area.hidden = true; };

  // Toggle version input visibility when Plantilla checkbox changes
  const $templCheck = document.getElementById('nx-task-is-template');
  const $verInput = document.getElementById('nx-task-version');
  $templCheck.addEventListener('change', () => {
    $verInput.style.display = $templCheck.checked ? '' : 'none';
    if ($templCheck.checked && !$verInput.value) $verInput.value = '1.0';
  });

  // SALV-PUERTA-API-T1: la tarea entra por la puerta (mision viva, clave y por que).
  const puerta = engancharCamposPuerta($form, 'nx-task-puerta', { $titulo: document.getElementById('nx-task-title') });

  $form.onsubmit = async (e) => {
    e.preventDefault();
    $error.hidden = true;

    const title = document.getElementById('nx-task-title').value.trim();
    const project = document.getElementById('nx-task-project').value;

    const $projSelect = document.getElementById('nx-task-project');
    $projSelect.classList.remove('input-error');

    if (!title) { $error.textContent = 'Titulo requerido'; $error.hidden = false; return; }
    if (!project) {
      $error.textContent = 'El proyecto es obligatorio para crear una tarea';
      $error.hidden = false;
      $projSelect.classList.add('input-error');
      $projSelect.focus();
      return;
    }

    const description = document.getElementById('nx-task-desc').value.trim();
    const rawTags = (document.getElementById('nx-task-tags').value || '')
      .split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    const isTempl = document.getElementById('nx-task-is-template').checked;
    if (isTempl && !rawTags.includes('template')) rawTags.push('template');
    const tags = [...new Set(rawTags)];
    const versionVal = document.getElementById('nx-task-version').value.trim();
    const metadata = isTempl && versionVal ? { version: versionVal } : undefined;
    const p = puerta.leer();
    if (!p.ok) { $error.textContent = p.error; $error.hidden = false; return; }
    const result = await TaskApiClient.create({ title, description: description || undefined, project, status: 'pending', priority: 'medium', tags: tags.length ? tags : undefined, metadata, ...p.campos });

    if (result.ok) {
      $area.hidden = true;
      $form.reset();
      currentPage = 1;
      await loadPage(1);
    } else {
      $error.textContent = motivoPuerta(result);
      $error.hidden = false;
    }
  };
}

function setupListHandlers() {
  const $list = document.getElementById('nx-task-list');

  // --- Shared module handles: title edit, desc save, step edit/delete/add/checkbox ---
  bindTaskDetailEvents($list, {
    getTask(taskId) {
      return taskDetailCache.get(taskId) || null;
    },
    onSaved(taskId, field, value) {
      const row = $list.querySelector(`.task-row[data-id="${taskId}"]`);
      if (!row) return;
      if (field === 'title') {
        const titleEl = row.querySelector('.artifact-item-title');
        if (titleEl) {
          const cached = taskDetailCache.get(taskId);
          const tags = cached?.tags || [];
          titleEl.innerHTML = (value || '').replace(/</g, '&lt;') + ' ' + renderTagBadges(tags);
        }
      }
      if (field === 'description') {
        let previewEl = row.querySelector('.nx-task-desc-preview');
        if (value) {
          const text = value.length > 120 ? value.slice(0, 120) + '\u2026' : value;
          if (previewEl) { previewEl.textContent = text; }
          else {
            const titleEl = row.querySelector('.artifact-item-title');
            if (titleEl) {
              const div = document.createElement('div');
              div.className = 'nx-task-desc-preview';
              div.style.cssText = 'font-size:0.875rem;color:#8b949e;margin-top:2px;line-height:1.3;overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
              div.textContent = text;
              titleEl.insertAdjacentElement('afterend', div);
            }
          }
        } else if (previewEl) {
          previewEl.remove();
        }
      }
    },
    onReload(taskId) {
      taskDetailCache.delete(taskId);
      const detail = document.getElementById(`nx-task-detail-${taskId}`);
      if (!detail) return;
      detail.dataset.loaded = '';
      detail.hidden = false;
      detail.innerHTML = '<p class="empty-state" style="font-size:0.875rem">Actualizando...</p>';
      TaskApiClient.getById(taskId).then(res => {
        if (!res.ok) {
          detail.innerHTML = `<p class="empty-state" style="font-size:0.875rem">Error: ${res.error}</p>`;
          return;
        }
        const task = normalizeTask(res.data?.data || res.data);
        taskDetailCache.set(taskId, task);
        detail.innerHTML = renderTaskDetailHTML(task);
        detail.dataset.loaded = 'true';
      });
    }
  });

  // --- Status change (view-specific: NXS completion/revert) ---
  $list.addEventListener('change', async (e) => {
    if (!e.target.classList.contains('status-select')) return;
    const id = e.target.dataset.id;
    const newStatus = e.target.value;
    if (!id || !newStatus) return;

    // Guard: templates cannot change status (use "Usar" to clone instead)
    const cached = taskDetailCache.get(id);
    if (cached && (cached.tags || []).includes('template')) {
      e.target.value = '';
      return;
    }

    e.target.disabled = true;
    const result = await TaskApiClient.updateStatus(id, newStatus);
    if (result.ok) {
      // Check mission FIRST (la API paga al cerrar, PR 435; aqui solo el aviso), then reload
      if (newStatus === 'completed') {
        const { completadas: completed, rechazadas } = await RewardService.checkMissionCompletion(id);
        if (rechazadas.length > 0) avisarCierre(rechazadas[0].frase, rechazadas[0].motivo);
        if (completed.length > 0) showRewardToast(completed);
      } else {
        const reverted = await RewardService.checkMissionRevert(id);
        if (reverted.length > 0) showRevertToast(reverted);
      }
      await loadPage(currentPage);
    } else {
      // SALV-DEPENDENCIAS-T1: una bloqueada (409 TAREA_BLOQUEADA) se dice con lo que espera.
      avisar(motivoTarea(result), { tono: 'error' });
      e.target.value = '';
      e.target.disabled = false;
    }
  });

  // --- Toggle expand + delete task (view-specific) ---
  $list.addEventListener('click', async (e) => {
    // Use template (clone)
    const useBtn = e.target.closest('[data-use-template]');
    if (useBtn) {
      e.stopPropagation();
      const templateId = useBtn.dataset.useTemplate;
      useBtn.disabled = true;
      useBtn.textContent = '...';
      const res = await TaskApiClient.getById(templateId);
      if (res.ok) {
        const tmpl = normalizeTask(res.data?.data || res.data);
        const cloneSteps = (tmpl.steps || []).map((s, i) => ({
          name: s.name ?? s.Name ?? '', completed: false, order: i
        }));
        const cloneTags = (tmpl.tags || []).filter(t => t !== 'template');
        const payload = {
          title: tmpl.title + ' (copia)',
          description: tmpl.description || '',
          project: tmpl.project || 'General',
          status: 'pending',
          priority: tmpl.priority || 'medium',
          tags: cloneTags,
          steps: cloneSteps,
          metadata: { template_source: templateId, cloned_at: new Date().toISOString() }
        };
        // SALV-PUERTA-API-T1: la copia tambien entra por la puerta; la persona elige mision, clave y por que.
        const campos = await pedirPuertaEnDialogo({
          titulo: 'Usar la plantilla: ' + tmpl.title,
          explicacion: 'La copia es una tarea nueva: va en una mision viva y sale con codigo CLAVE-Tn.',
          claveInicial: claveSugerida(tmpl.title),
          porQueInicial: 'Copia de la plantilla "' + tmpl.title + '"',
          boton: 'Crear la copia'
        });
        if (!campos) {
          useBtn.disabled = false;
          useBtn.textContent = '▶ Usar';
          return;
        }
        const createRes = await TaskApiClient.create({ ...payload, ...campos });
        if (createRes.ok) {
          currentTypeFilter = 'regular';
          document.querySelectorAll('[data-type]').forEach(b => b.classList.remove('active'));
          document.querySelector('[data-type="regular"]')?.classList.add('active');
          currentPage = 1;
          await loadPage(1);
        } else {
          avisar('Error al clonar: ' + motivoPuerta(createRes), { tono: 'error' });
        }
      }
      useBtn.disabled = false;
      useBtn.textContent = '\u25B6 Usar';
      return;
    }

    // Delete task (check FIRST, before toggle intercepts)
    const deleteBtn = e.target.closest('.btn-delete');
    if (deleteBtn) {
      const id = deleteBtn.dataset.id;
      if (!await confirmar({ titulo: 'Eliminar esta tarea', texto: 'No se puede deshacer.', aceptar: 'Eliminar tarea', peligro: true })) return;
      deleteBtn.disabled = true;
      const result = await TaskApiClient.remove(id);
      if (result.ok) {
        await loadPage(currentPage);
      } else {
        avisar(`Error: ${result.error}`, { tono: 'error' });
        deleteBtn.disabled = false;
      }
      return;
    }

    // Toggle expand (lazy-load via getById, render with shared module)
    const toggleBtn = e.target.closest('[data-toggle-task]');
    if (toggleBtn) {
      // No expandir si se hizo click en select o button dentro de task-actions
      if (e.target.closest('.task-actions')) return;
      const taskId = toggleBtn.dataset.toggleTask;
      const detail = document.getElementById(`nx-task-detail-${taskId}`);
      if (!detail || !document.body.contains(detail)) return;
      const icon = $list.querySelector(`[data-expand-icon="${taskId}"]`);
      if (!detail.hidden) {
        detail.hidden = true;
        if (icon) icon.style.transform = 'rotate(0deg)';
        return;
      }
      detail.hidden = false;
      if (icon) icon.style.transform = 'rotate(90deg)';
      if (detail.dataset.loaded) return;
      detail.innerHTML = '<p class="empty-state" style="font-size:0.875rem">Cargando...</p>';
      const res = await TaskApiClient.getById(taskId);
      if (!res.ok) {
        detail.innerHTML = `<p class="empty-state" style="font-size:0.875rem">Error: ${res.error}</p>`;
        return;
      }
      const task = normalizeTask(res.data?.data || res.data);
      taskDetailCache.set(taskId, task);
      detail.innerHTML = renderTaskDetailHTML(task);
      detail.dataset.loaded = 'true';
      return;
    }

    // (delete handler moved to top of click listener)
  });
}

async function loadPage(page) {
  const $list = document.getElementById('nx-task-list');
  const $pager = document.getElementById('nx-task-pager');
  if (!$list) return;

  $list.innerHTML = '<p class="empty-state">Cargando...</p>';
  currentPage = page;

  const apiStatus = currentFilter === 'all' ? '' : currentFilter;
  const result = await TaskApiClient.list(page, 50, apiStatus);

  if (!result.ok) {
    $list.innerHTML = `<p class="empty-state">Error: ${result.error}</p>`;
    return;
  }

  const payload = result.data?.data || result.data;
  let items = (payload?.items || []).map(normalizeTask);

  items.sort((a, b) => {
    let cmp = 0;
    if (currentSort === 'priority') {
      cmp = (PRIORITY_ORDER[a.priority] || 0) - (PRIORITY_ORDER[b.priority] || 0);
    } else if (currentSort === 'dueDate') {
      const da = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
      const db = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
      cmp = da - db;
    } else {
      cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    }
    return currentSortDir === 'desc' ? -cmp : cmp;
  });

  if (currentFilter !== 'all') {
    items = items.filter(t => t.status === currentFilter);
  }

  // Type filter
  if (currentTypeFilter === 'templates') {
    items = items.filter(t => (t.tags || []).includes('template'));
  } else if (currentTypeFilter === 'regular') {
    items = items.filter(t => !(t.tags || []).includes('template'));
  }

  // Tag filters (AND)
  if (currentTagFilters.length > 0) {
    items = items.filter(t => currentTagFilters.every(tag => (t.tags || []).includes(tag)));
  }

  // Build dynamic tag chips
  const allTags = new Set();
  (payload?.items || []).forEach(t => {
    ((t.tags ?? t.Tags) || []).forEach(tag => { if (tag !== 'template') allTags.add(tag); });
  });
  const $chips = document.getElementById('nx-tag-chips');
  if ($chips) {
    $chips.innerHTML = allTags.size === 0 ? '<span style="font-size:0.875rem;color:#484f58">--</span>'
      : [...allTags].sort().map(tag =>
        `<button class="btn-tag-filter${currentTagFilters.includes(tag) ? ' active' : ''}" data-tag-chip="${tag}">${tag}</button>`
      ).join('');
  }

  const filterLabel = currentFilter === 'all'
    ? '' : ` (${STATUS_LABELS[currentFilter] || currentFilter})`;

  if (items.length === 0) {
    $list.innerHTML = `<p class="empty-state">No hay tareas${filterLabel}</p>`;
    $pager.innerHTML = '';
    return;
  }

  // Compute template effectiveness stats from all loaded items
  const allItems = (payload?.items || []).map(normalizeTask);
  const templateStats = new Map();
  for (const t of allItems) {
    const src = t.metadata?.template_source;
    if (src) {
      const s = templateStats.get(src) || { used: 0, completed: 0 };
      s.used++;
      if (t.status === 'completed') s.completed++;
      templateStats.set(src, s);
    }
  }

  $list.innerHTML = items.map(t => taskRow(t, templateStats)).join('');

  const totalPages = payload?.totalPages || 1;
  const pg = payload?.page || page;

  if (totalPages > 1) {
    let html = '';
    for (let p = 1; p <= totalPages; p++) {
      const cls = p === pg ? 'btn-primary' : 'btn-ghost';
      html += `<button class="${cls} btn-sm page-btn" data-page="${p}">${p}</button>`;
    }
    $pager.innerHTML = html;
    $pager.querySelectorAll('.page-btn').forEach(btn => {
      btn.onclick = () => loadPage(Number(btn.dataset.page));
    });
  } else {
    $pager.innerHTML = '';
  }
}

function showRevertToast(missions) {
  const totalNxs = missions.reduce((s, m) => s + (m.rewardNexos || 0), 0);
  const names = missions.map(m => m.title || m.code).join(', ');
  const toast = document.createElement('div');
  toast.className = 'reward-toast';
  toast.style.borderColor = '#f85149';
  toast.innerHTML = `
    <strong>Mision revertida</strong><br>
    ${names}<br>
    <span class="nxs-earned" style="color:#f85149">-${totalNxs} NXS</span>`;
  document.body.appendChild(toast);
  requestAnimationFrame(() => toast.classList.add('visible'));
  setTimeout(() => { toast.classList.remove('visible'); setTimeout(() => toast.remove(), 400); }, 4000);
}

function showRewardToast(missions) {
  const totalNxs = missions.reduce((s, m) => s + (m.rewardNexos || 0), 0);
  const names = missions.map(m => m.title || m.code).join(', ');

  const toast = document.createElement('div');
  toast.className = 'reward-toast';
  toast.innerHTML = `
    <strong>Mision completada!</strong><br>
    ${names}<br>
    <span class="nxs-earned">+${totalNxs} NXS</span>`;
  document.body.appendChild(toast);

  requestAnimationFrame(() => toast.classList.add('visible'));
  setTimeout(() => {
    toast.classList.remove('visible');
    setTimeout(() => toast.remove(), 400);
  }, 4000);
}
